import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { Statement } from './historyApi'
import { StatementDocument } from './StatementDocument'

const PRINT_ROOT = 'xsib-statement-print'

const PRINT_CSS = `
  .${PRINT_ROOT} { display: none; }
  @media print {
    @page { size: A4 portrait; margin: 12mm; }
    html, body { background: #fff !important; overflow: visible !important; height: auto !important; }
    body > *:not(.${PRINT_ROOT}) { display: none !important; }
    .${PRINT_ROOT} { display: block; }
  }
`

async function imagesDecoded(root: HTMLElement | null): Promise<boolean> {
  const images = Array.from(root?.querySelectorAll('img') ?? [])
  const results = await Promise.allSettled(images.map((image) => image.decode()))
  return results.every((result) => result.status === 'fulfilled')
}

interface StatementPrintProps {
  statement: Statement
  onDone: () => void
}

export function StatementPrint({ statement, onDone }: StatementPrintProps) {
  const root = useRef<HTMLDivElement>(null)
  const [logoFailed, setLogoFailed] = useState(false)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    const style = document.createElement('style')
    style.textContent = PRINT_CSS
    document.head.appendChild(style)
    return () => style.remove()
  }, [])

  useEffect(() => {
    let live = true
    void imagesDecoded(root.current).then((ok) => {
      if (!live) return
      setLogoFailed(!ok)
      setReady(true)
    })
    return () => {
      live = false
    }
  }, [])

  useEffect(() => {
    if (!ready) return
    window.addEventListener('afterprint', onDone)
    window.print()
    return () => window.removeEventListener('afterprint', onDone)
  }, [ready, onDone])

  const shown = logoFailed ? { ...statement, company: { ...statement.company, logoUrl: null } } : statement

  return createPortal(
    <div ref={root} className={PRINT_ROOT}>
      <StatementDocument statement={shown} />
    </div>,
    document.body,
  )
}
